import styled from 'styled-components';

import { Button } from '../../../cdl/Button/Button';
import { IconButton } from '../../../cdl/IconButton/IconButton';
import { ProgressBar } from '../../../cdl/ProgressBar/ProgressBar';
import { Text } from '../../../cdl/Text/Text';
import { translate } from '../../../common/helpers/translate.helper';
import { IconUpload } from '../../../common/icons/cdl/Upload';
import { IconX } from '../../../common/icons/cdl/X';
import { Box } from '../../../common/ui/Box';
import { theme } from '../../../common/ui/theme';

import { usePriceListDropzone } from './usePriceListDropzone';

interface DropZoneWrapperProps {
    isDragAccept: boolean;
    isDragReject: boolean;
    isDragActive: boolean;
}

const getBorderStyle = (props: DropZoneWrapperProps) => {
    if (props.isDragReject || props.isDragAccept || props.isDragActive) {
        return 'dashed';
    }
    return 'solid';
};

const getBorderColor = (props: DropZoneWrapperProps) => {
    if (props.isDragReject) {
        return theme.colors.negative.emphasis;
    }
    if (props.isDragAccept || props.isDragActive) {
        return theme.colors.accent.emphasis;
    }
    return theme.colors.border.subtle;
};

const getBackgroundColor = (props: DropZoneWrapperProps) => {
    if (props.isDragReject) {
        return theme.colors.negative.background;
    }
    if (props.isDragAccept || props.isDragActive) {
        return theme.colors.accent.background;
    }
    return 'transparent';
};

const DropZoneWrapper = styled(Box)<DropZoneWrapperProps>`
    display: flex;
    flex-direction: column;
    gap: ${theme.space[8]};
    padding: ${theme.space[16]};
    border-radius: 6px;
    border-width: 1px;
    border-style: ${(props) => getBorderStyle(props)};
    border-color: ${(props) => getBorderColor(props)};
    background-color: ${(props) => getBackgroundColor(props)};
    transition: all 0.2s ease-in-out;
`;

interface PriceListUploadProps {
    onFileSelected: (file: File) => void;
    uploadProgress?: number;
    onCancel?: () => void;
    disabled?: boolean;
}

export const PriceListUpload = ({ onFileSelected, uploadProgress, onCancel, disabled }: PriceListUploadProps) => {
    const { getRootProps, getInputProps, open, isDragAccept, isDragReject, isDragActive } =
        usePriceListDropzone(onFileSelected);

    return (
        <DropZoneWrapper
            {...getRootProps()}
            isDragAccept={isDragAccept}
            isDragReject={isDragReject}
            isDragActive={isDragActive}
        >
            <input {...getInputProps()} />
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Text variant="fieldLabel">{translate('contracts.priceLists.priceList')}</Text>
                {onCancel ? (
                    <IconButton
                        Icon={IconX}
                        emphasis="none"
                        size="medium"
                        type="button"
                        onClick={onCancel}
                        tooltipLabel={translate('contracts.priceLists.cancel')}
                    />
                ) : null}
            </Box>
            <Text variant="extraSmall" color="foreground.subtle">
                {translate('contracts.priceLists.acceptedFileTypes')}
            </Text>
            <Box>
                {uploadProgress !== undefined ? (
                    <ProgressBar value={uploadProgress} />
                ) : (
                    <Button type="button" LeadingIcon={IconUpload} onClick={open} disabled={disabled}>
                        {translate('contracts.priceLists.upload')}
                    </Button>
                )}
            </Box>
        </DropZoneWrapper>
    );
};
